import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "./creator.css";
import { api, downloadAuthed, fmtDate, usd } from "./api";
import { CreatorBar } from "./CreatorLanding";

const TABS = [
  ["overview", "Overview"],
  ["creators", "Creators"],
  ["events", "Events"],
  ["rules", "Commission rates"],
  ["commissions", "Commissions"],
  ["payouts", "Payouts"],
  ["campaigns", "Campaigns"],
  ["analytics", "Analytics"],
  ["audit", "Audit log"],
];
const FLAG_COPY = {
  self_referral: "Self-referral",
  no_valid_click: "No tracked click in window",
  event_mismatch: "Different event",
  creator_not_earning: "Creator not approved",
  link_inactive: "Link paused",
  cost_unverified: "Cost not verified (fee-only basis)",
  event_not_in_program: "Promo code used on an event not open to creators",
};

function useAsync(fn, deps) {
  const [state, setState] = useState({ loading: true });
  const run = useCallback(() => {
    setState((s) => ({ ...s, loading: true }));
    return fn()
      .then((data) => setState({ loading: false, data }))
      .catch((error) => setState({ loading: false, error: error.message }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => {
    run();
  }, [run]);
  return [state, run];
}

function Notice({ state }) {
  if (state.loading) return <p className="cp-fine">Loading…</p>;
  if (state.error) return <div className="cp-error">{state.error}</div>;
  return null;
}

export default function AdminCreators() {
  const [tab, setTab] = useState("overview");
  const [flash, setFlash] = useState("");
  const say = (m) => {
    setFlash(m);
    setTimeout(() => setFlash(""), 2600);
  };
  const act = async (fn, ok) => {
    try {
      await fn();
      if (ok) say(ok);
      return true;
    } catch (err) {
      say(err.message);
      return false;
    }
  };

  useEffect(() => {
    document.title = "Creator Partners admin — InstaPass";
  }, []);

  return (
    <div className="cp">
      <CreatorBar
        right={
          <Link to="/admin/dashboard" className="cp-link">
            Main admin
          </Link>
        }
      />
      <div className="cp-wrap cp-dash-head">
        <h1 className="display">Creator Partners</h1>
        <p>Applications, events, rates, verified sales and payouts.</p>
      </div>
      <nav className="cp-tabs" aria-label="Admin sections">
        <div className="cp-wrap" role="tablist">
          {TABS.map(([id, label]) => (
            <button key={id} role="tab" aria-selected={tab === id} className="cp-tab" onClick={() => setTab(id)}>
              {label}
            </button>
          ))}
        </div>
      </nav>
      <main className="cp-wrap cp-panel">
        {tab === "overview" && <Overview />}
        {tab === "creators" && <Creators act={act} />}
        {tab === "events" && <Events act={act} />}
        {tab === "rules" && <Rules act={act} />}
        {tab === "commissions" && <Commissions act={act} />}
        {tab === "payouts" && <Payouts act={act} />}
        {tab === "campaigns" && <Campaigns act={act} />}
        {tab === "analytics" && <Analytics />}
        {tab === "audit" && <Audit />}
      </main>
      {flash && (
        <div className="cp-toast" role="status">
          {flash}
        </div>
      )}
    </div>
  );
}

function Overview() {
  const [s] = useAsync(() => api("/admin/overview"), []);
  if (!s.data) return <Notice state={s} />;
  const t = s.data.totals;
  const kpis = [
    ["Total creators", t.creators],
    ["Pending applications", t.pendingApplications],
    ["Active creators", t.activeCreators],
    ["QR scans", t.qrScans],
    ["Referral clicks", t.referralClicks],
    ["Referred tickets sold", t.ticketsSold],
    ["Attributed revenue", usd(t.attributedRevenueCents)],
    ["Commissions earned", usd(t.commissionsEarnedCents)],
    ["Pending commissions", usd(t.pendingCommissionsCents)],
    ["Approved, unpaid", usd(t.availablePayoutCents)],
  ];
  return (
    <div className="cp-admin-grid">
      <div className="cp-kpis">
        {kpis.map(([l, v]) => (
          <div key={l}>
            <b>{v}</b>
            <span>{l}</span>
          </div>
        ))}
      </div>
      <h2 style={{ fontSize: 20, fontWeight: 800, margin: "12px 0 0" }}>Top performing creators</h2>
      {s.data.topCreators.length ? (
        <table className="cp-table">
          <thead>
            <tr>
              <th>Creator</th>
              <th>Orders</th>
              <th>Tickets</th>
              <th>Revenue</th>
              <th>Commission</th>
            </tr>
          </thead>
          <tbody>
            {s.data.topCreators.map((r) => (
              <tr key={r._id}>
                <td>
                  {r.creator?.displayName} <span className="cp-fine">{r.creator?.creatorId}</span>
                </td>
                <td>{r.orders}</td>
                <td>{r.tickets}</td>
                <td>{usd(r.revenueCents)}</td>
                <td>{usd(r.commissionCents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="cp-fine">No verified creator sales yet.</p>
      )}
    </div>
  );
}

function Creators({ act }) {
  const [filter, setFilter] = useState("PENDING");
  const [s, reload] = useAsync(() => api(`/admin/creators${filter ? `?status=${filter}` : ""}`), [filter]);
  const setStatus = async (c, status) => {
    let reason;
    if (status === "REJECTED" || status === "SUSPENDED") {
      reason = window.prompt(`Reason sent to ${c.fullName} (optional)`) || undefined;
    }
    if (await act(() => api(`/admin/creators/${c._id}/status`, { method: "POST", body: { status, reason } }), `${c.creatorId} → ${status}`)) reload();
  };
  const setTier = async (c, tier) => {
    if (await act(() => api(`/admin/creators/${c._id}/tier`, { method: "POST", body: { tier } }), `${c.creatorId} tier → ${tier}`)) reload();
  };
  return (
    <>
      <div className="cp-actions" style={{ marginBottom: 18 }}>
        {["PENDING", "APPROVED", "ACTIVE", "SUSPENDED", "REJECTED", ""].map((f) => (
          <button key={f || "all"} className="cp-chip" aria-pressed={filter === f} onClick={() => setFilter(f)}>
            {f || "All"}
          </button>
        ))}
        <button className="cp-btn small ghost" onClick={() => act(() => downloadAuthed("/admin/export/creators.csv", "instapass-creators.csv"))}>
          Export CSV
        </button>
        <button className="cp-btn small ghost" onClick={() => act(() => api("/admin/beehiiv/sync", { method: "POST" }), "Synced to beehiiv")}>
          Sync to beehiiv
        </button>
      </div>
      <Notice state={s} />
      {s.data && (
        <div className="cp-table-scroll">
          <table className="cp-table">
            <thead>
              <tr>
                <th>Creator</th>
                <th>Socials</th>
                <th>City</th>
                <th>Audience</th>
                <th>Applied</th>
                <th>Status</th>
                <th>Tier</th>
                <th>TEVO promo code</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {s.data.creators.map((c) => (
                <tr key={c._id}>
                  <td>
                    <b>{c.fullName}</b>
                    <div className="cp-fine" style={{ margin: 0 }}>
                      {c.creatorId} · {c.email}
                    </div>
                  </td>
                  <td>
                    {c.tiktok && (
                      <a href={`https://www.tiktok.com/@${c.tiktok}`} target="_blank" rel="noreferrer">
                        TikTok @{c.tiktok}
                      </a>
                    )}
                    {c.instagram && (
                      <div>
                        <a href={`https://instagram.com/${c.instagram}`} target="_blank" rel="noreferrer">
                          IG @{c.instagram}
                        </a>
                      </div>
                    )}
                  </td>
                  <td>{c.city}</td>
                  <td>
                    {c.audienceSize}
                    <div className="cp-fine" style={{ margin: 0 }}>
                      {c.contentCategory}
                    </div>
                  </td>
                  <td>{fmtDate(c.createdAt)}</td>
                  <td>
                    <span className={`cp-status ${c.status}`}>{c.status}</span>
                  </td>
                  <td>
                    <select className="cp-select" style={{ minHeight: 36, width: 110 }} value={c.tier} onChange={(e) => setTier(c, e.target.value)} aria-label="Tier">
                      {["starter", "active", "top", "featured"].map((t) => (
                        <option key={t}>{t}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <PromoCell c={c} act={act} reload={reload} />
                  </td>
                  <td>
                    <div className="cp-admin-actions">
                      {c.status === "PENDING" && (
                        <>
                          <button className="cp-btn small" onClick={() => setStatus(c, "APPROVED")}>
                            Approve
                          </button>
                          <button className="cp-btn small ghost" onClick={() => setStatus(c, "REJECTED")}>
                            Reject
                          </button>
                        </>
                      )}
                      {["APPROVED", "ACTIVE"].includes(c.status) && (
                        <button className="cp-btn small ghost" onClick={() => setStatus(c, "SUSPENDED")}>
                          Suspend
                        </button>
                      )}
                      {["SUSPENDED", "REJECTED"].includes(c.status) && (
                        <button className="cp-btn small ghost" onClick={() => setStatus(c, "APPROVED")}>
                          Reinstate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!s.data.creators.length && <p className="cp-fine">No creators with this status.</p>}
        </div>
      )}
    </>
  );
}

function PromoCell({ c, act, reload }) {
  const current = c.tevoPromo?.code || c.tevoPromoCode || "";
  const [code, setCode] = useState(current);
  const changed = code.trim() !== current;
  const save = async () => {
    let r;
    const ok = await act(async () => { r = await api(`/admin/creators/${c._id}/promo-code`, { method: "POST", body: { code } }); });
    if (!ok) return;
    const how = r?.delivery === "beehiiv" ? " · beehiiv is emailing it" : r?.delivery === "email" ? " · emailed to the creator" : "";
    act(async () => {}, code ? `"${code.trim()}" assigned to ${c.creatorId}${how}` : "Promo code removed");
    reload();
  };
  return (
    <div style={{ display: "flex", gap: 6, alignItems: "center", minWidth: 210 }}>
      <input className="cp-input" style={{ minHeight: 36, fontSize: 14 }} value={code} onChange={(e) => setCode(e.target.value)} placeholder="Exactly as in TEVO" aria-label={`TEVO promo code for ${c.creatorId}`} />
      {changed && (
        <button className="cp-btn small" onClick={save}>
          {code ? "Assign" : "Clear"}
        </button>
      )}
    </div>
  );
}

function Events({ act }) {
  const [s, reload] = useAsync(() => api("/admin/events"), []);
  const [form, setForm] = useState({ eventId: "", perTicketDollars: "", authorizationNote: "", imageUrl: "", promoCopy: "" });
  const [busy, setBusy] = useState(false);
  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    if (await act(() => api("/admin/events/tevo", { method: "POST", body: form }), "Event added")) {
      setForm({ eventId: "", perTicketDollars: "", authorizationNote: "", imageUrl: "", promoCopy: "" });
      reload();
    }
    setBusy(false);
  };
  const patch = async (ev, body, ok) => {
    if (await act(() => api(`/admin/events/${ev._id}`, { method: "PATCH", body }), ok)) reload();
  };
  const changeRate = async (ev) => {
    const v = window.prompt(`What do creators earn per ticket for ${ev.name}? (dollars)`, ev.perTicketCents ? String(ev.perTicketCents / 100) : "");
    if (v == null) return;
    if (await act(() => api(`/admin/events/${ev._id}/rate`, { method: "POST", body: { perTicketDollars: v } }), "Per-ticket amount updated — recalculate pending commissions to apply it to earlier sales")) reload();
  };
  return (
    <div className="cp-admin-grid">
      <form className="cp-form" onSubmit={add} style={{ maxWidth: 720 }}>
        <h2 style={{ fontSize: 20, fontWeight: 800, margin: 0 }}>Open a Ticket Evolution event to creators</h2>
        <p className="cp-fine" style={{ margin: 0 }}>
          Use the number at the end of the event's InstaPass URL (/tickets/group.details/<b>123456</b>). Name, date and venue come from
          Ticket Evolution.
        </p>
        <div className="cp-two">
          <div className="cp-field">
            <label htmlFor="ev-id">TEVO event id</label>
            <input id="ev-id" className="cp-input" inputMode="numeric" value={form.eventId} onChange={(e) => setForm({ ...form, eventId: e.target.value })} required />
          </div>
          <div className="cp-field">
            <label htmlFor="ev-rate">Creators earn per ticket ($)</label>
            <span className="help">Shown on the event card. Keep it under InstaPass's margin on these tickets.</span>
            <input id="ev-rate" className="cp-input" inputMode="decimal" placeholder="5" value={form.perTicketDollars} onChange={(e) => setForm({ ...form, perTicketDollars: e.target.value })} required />
          </div>
        </div>
        <div className="cp-field">
          <div className="cp-field">
            <label htmlFor="ev-img">Artwork URL</label>
            <input id="ev-img" className="cp-input" value={form.imageUrl} onChange={(e) => setForm({ ...form, imageUrl: e.target.value })} placeholder="https://" />
          </div>
        </div>
        <div className="cp-field">
          <label htmlFor="ev-auth">Promotion authorized by</label>
          <span className="help">Required — who cleared these tickets for creator promotion, and when.</span>
          <input id="ev-auth" className="cp-input" value={form.authorizationNote} onChange={(e) => setForm({ ...form, authorizationNote: e.target.value })} required />
        </div>
        <div className="cp-field">
          <label htmlFor="ev-copy">Promo copy for creators</label>
          <input id="ev-copy" className="cp-input" value={form.promoCopy} onChange={(e) => setForm({ ...form, promoCopy: e.target.value })} />
        </div>
        <div>
          <button className="cp-btn" disabled={busy}>
            {busy ? "Adding…" : "Add event"}
          </button>
        </div>
      </form>
      <Notice state={s} />
      {s.data && (
        <div className="cp-table-scroll">
          <table className="cp-table">
            <thead>
              <tr>
                <th>Event</th>
                <th>Date</th>
                <th>Per ticket</th>
                <th>Authorized by</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {s.data.events.map((ev) => (
                <tr key={ev._id}>
                  <td>
                    <b>{ev.name}</b>
                    <div className="cp-fine" style={{ margin: 0 }}>
                      {ev.venue} · TEVO {ev.eventId}
                    </div>
                  </td>
                  <td>{fmtDate(ev.date)}</td>
                  <td>
                    {ev.perTicketCents ? usd(ev.perTicketCents) : <span className="cp-status pending">not set</span>}
                    <div>
                      <button className="cp-link" style={{ fontSize: 12 }} onClick={() => changeRate(ev)}>
                        Change
                      </button>
                    </div>
                  </td>
                  <td>{ev.authorizationNote}</td>
                  <td>
                    <span className={`cp-status ${ev.active ? "active" : "retired"}`}>{ev.active ? "open" : "closed"}</span>
                  </td>
                  <td>
                    <button className="cp-btn small ghost" onClick={() => patch(ev, { active: !ev.active }, ev.active ? "Closed to creators" : "Reopened")}>
                      {ev.active ? "Close" : "Reopen"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!s.data.events.length && <p className="cp-fine">No events open to creators yet.</p>}
        </div>
      )}
    </div>
  );
}

const rateText = (r) => ((r.basis || "net_fee") === "per_ticket" ? `${usd(r.perTicketCents)}/ticket` : `${r.ratePercent}% of net fee`);

function Rules({ act }) {
  const [s, reload] = useAsync(() => api("/admin/rules"), []);
  const [events] = useAsync(() => api("/admin/events"), []);
  const [form, setForm] = useState({ scope: "event", perTicketDollars: "", creatorEventId: "", tier: "starter", name: "" });
  const setStatus = async (r, status) => {
    if (status === "active" && !window.confirm(`Activate ${rateText(r)} for ${r.name}? New verified sales will earn at this rate.`)) return;
    if (await act(() => api(`/admin/rules/${r._id}/status`, { method: "POST", body: { status } }), `Rule ${status}`)) reload();
  };
  const create = async (e) => {
    e.preventDefault();
    if (await act(() => api("/admin/rules", { method: "POST", body: form }), "Rule proposed — activate it to apply")) reload();
  };
  return (
    <div className="cp-admin-grid">
      <div className="cp-note">
        Creators earn a flat amount per ticket. Set it on each event in the <b>Events</b> tab — that's the normal way. Rules here
        are for exceptions (a tier default or a single creator). If an amount ever exceeds what InstaPass earned on an order, the
        commission is capped at that margin and marked <b>capped</b>. Most specific wins: creator → event → campaign → tier.
      </div>
      <div className="cp-actions">
        <button className="cp-btn small ghost" onClick={async () => (await act(() => api("/admin/rules/seed-proposed", { method: "POST" }), "Proposed tiers added")) && reload()}>
          Add proposed $3 / $4 / $5 tier defaults
        </button>
        <button className="cp-btn small ghost" onClick={() => act(() => api("/admin/commissions/recalculate", { method: "POST" }), "Pending commissions recalculated")}>
          Recalculate pending commissions
        </button>
      </div>
      <Notice state={s} />
      {s.data && (
        <table className="cp-table">
          <thead>
            <tr>
              <th>Rule</th>
              <th>Applies to</th>
              <th>Rate</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {s.data.rules.map((r) => (
              <tr key={r._id}>
                <td>{r.name}</td>
                <td>{r.scope === "tier" ? `Tier: ${r.tier}` : r.scope === "event" ? r.creatorEvent?.name : r.scope === "creator" ? r.creator?.creatorId : r.scope}</td>
                <td>{rateText(r)}</td>
                <td>
                  <span className={`cp-status ${r.status}`}>{r.status}</span>
                  {r.approvedAt && <div className="cp-fine" style={{ margin: 0 }}>since {fmtDate(r.approvedAt)}</div>}
                </td>
                <td>
                  <div className="cp-admin-actions">
                    {r.status !== "active" && (
                      <button className="cp-btn small" onClick={() => setStatus(r, "active")}>
                        Activate
                      </button>
                    )}
                    {r.status !== "retired" && (
                      <button className="cp-btn small ghost" onClick={() => setStatus(r, "retired")}>
                        Retire
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <form className="cp-inline-form" onSubmit={create}>
        <div className="cp-field">
          <label htmlFor="r-scope">New rule for</label>
          <select id="r-scope" className="cp-select" value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })}>
            <option value="event">An event</option>
            <option value="tier">A creator tier</option>
          </select>
        </div>
        {form.scope === "event" ? (
          <div className="cp-field">
            <label htmlFor="r-ev">Event</label>
            <select id="r-ev" className="cp-select" value={form.creatorEventId} onChange={(e) => setForm({ ...form, creatorEventId: e.target.value, name: e.target.selectedOptions[0]?.text })} required>
              <option value="">Choose…</option>
              {events.data?.events.map((ev) => (
                <option key={ev._id} value={ev._id}>
                  {ev.name}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div className="cp-field">
            <label htmlFor="r-tier">Tier</label>
            <select id="r-tier" className="cp-select" value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value, name: `Tier ${e.target.value}` })}>
              {["starter", "active", "top", "featured"].map((t) => (
                <option key={t}>{t}</option>
              ))}
            </select>
          </div>
        )}
        <div className="cp-field" style={{ flex: "0 1 120px" }}>
          <label htmlFor="r-rate">$ per ticket</label>
          <input id="r-rate" className="cp-input" inputMode="decimal" value={form.perTicketDollars} onChange={(e) => setForm({ ...form, perTicketDollars: e.target.value })} required />
        </div>
        <button className="cp-btn">Propose rule</button>
      </form>
    </div>
  );
}

function Commissions({ act }) {
  const [status, setStatus] = useState("pending");
  const [s, reload] = useAsync(() => api(`/admin/commissions${status ? `?status=${status}` : ""}`), [status]);
  const [flagged] = useAsync(() => api("/admin/attributions?flagged=1"), []);
  const approve = async (c) => (await act(() => api(`/admin/commissions/${c._id}/approve`, { method: "POST" }), "Approved")) && reload();
  const voidIt = async (c) => {
    const reason = window.prompt("Reason for voiding");
    if (reason == null) return;
    (await act(() => api(`/admin/commissions/${c._id}/void`, { method: "POST", body: { reason } }), "Voided")) && reload();
  };
  return (
    <div className="cp-admin-grid">
      <div className="cp-actions">
        {["pending", "approved", "paid", "void", ""].map((f) => (
          <button key={f || "all"} className="cp-chip" aria-pressed={status === f} onClick={() => setStatus(f)}>
            {f || "All"}
          </button>
        ))}
        <button className="cp-btn small" onClick={async () => (await act(async () => { const r = await api("/admin/orders/sync", { method: "POST" }); if (r.errors) throw new Error(`Checked ${r.checked}, voided ${r.voided}, ${r.errors} errors`); }, "Order states checked with Ticket Evolution")) && reload()}>
          Check order states with TEVO
        </button>
        <button className="cp-btn small ghost" onClick={() => act(() => downloadAuthed("/admin/export/commissions.csv", "instapass-commissions.csv"))}>
          Export CSV
        </button>
      </div>
      <p className="cp-fine" style={{ margin: 0 }}>Run the TEVO check before approving — it voids commissions on cancelled or refunded orders.</p>
      <Notice state={s} />
      {s.data && (
        <div className="cp-table-scroll">
          <table className="cp-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Creator</th>
                <th>Event / order</th>
                <th>Tickets</th>
                <th>Net fee</th>
                <th>Per ticket</th>
                <th>Commission</th>
                <th>Order state</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {s.data.commissions.map((c) => (
                <tr key={c._id}>
                  <td>{fmtDate(c.createdAt)}</td>
                  <td>{c.creator?.displayName}</td>
                  <td>
                    {c.creatorEvent?.name}
                    <div className="cp-fine" style={{ margin: 0 }}>
                      {c.attribution?.orderKey}
                      {c.attribution?.flags?.includes("cost_unverified") && " · cost unverified"}
                    </div>
                  </td>
                  <td>{c.attribution?.quantity}</td>
                  <td title={`Revenue ${usd(c.basisBreakdown?.ticketRevenueCents)} − cost ${usd(c.basisBreakdown?.wholesaleCostCents)} + fee ${usd(c.basisBreakdown?.serviceFeeCents)} − discount ${usd(c.basisBreakdown?.discountCents)}`}>
                    {usd(c.basisCents)}
                  </td>
                  <td>{c.rule ? rateText(c) : "—"}</td>
                  <td>
                    {usd(c.amountCents)}
                    {c.capped && <div className="cp-fine" style={{ margin: 0, color: "#f5b335" }}>capped at margin</div>}
                  </td>
                  <td>{c.attribution?.orderState || "—"}</td>
                  <td>
                    <span className={`cp-status ${c.status}`}>{c.status}</span>
                    {c.statusReason && <div className="cp-fine" style={{ margin: 0 }}>{c.statusReason}</div>}
                  </td>
                  <td>
                    {c.status === "pending" && (
                      <div className="cp-admin-actions">
                        <button className="cp-btn small" onClick={() => approve(c)} disabled={!c.rule}>
                          Approve
                        </button>
                        <button className="cp-btn small ghost" onClick={() => voidIt(c)}>
                          Void
                        </button>
                      </div>
                    )}
                    {c.status === "approved" && !c.payout && (
                      <button className="cp-btn small ghost" onClick={() => voidIt(c)}>
                        Void
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!s.data.commissions.length && <p className="cp-fine">Nothing here.</p>}
        </div>
      )}
      <h2 style={{ fontSize: 20, fontWeight: 800, margin: "16px 0 0" }}>Flagged referrals (no commission)</h2>
      <Notice state={flagged} />
      {flagged.data && (
        <table className="cp-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Creator</th>
              <th>Order</th>
              <th>Why</th>
            </tr>
          </thead>
          <tbody>
            {flagged.data.attributions.map((a) => (
              <tr key={a._id}>
                <td>{fmtDate(a.createdAt)}</td>
                <td>{a.creator?.creatorId}</td>
                <td>{a.orderKey}</td>
                <td>{a.flags.map((f) => FLAG_COPY[f] || f).join(", ")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Payouts({ act }) {
  const [s, reload] = useAsync(() => api("/admin/payouts"), []);
  const [creators] = useAsync(() => api("/admin/creators"), []);
  const [creatorId, setCreatorId] = useState("");
  const draft = async (e) => {
    e.preventDefault();
    (await act(() => api("/admin/payouts", { method: "POST", body: { creatorId } }), "Payout drafted")) && reload();
  };
  const markPaid = async (p) => {
    const method = window.prompt("How was this paid? (e.g. Stripe Connect, Zelle)");
    if (!method) return;
    const reference = window.prompt("Payment reference / transaction id");
    if (!reference) return;
    (await act(() => api(`/admin/payouts/${p._id}/paid`, { method: "POST", body: { method, reference } }), "Recorded as paid")) && reload();
  };
  return (
    <div className="cp-admin-grid">
      <div className="cp-note">
        Payouts here are records only — nothing sends money. Pay creators through your payout provider, then record the method and
        reference. Collect each creator's W-9 / tax details before their first payout.
      </div>
      <form className="cp-inline-form" onSubmit={draft}>
        <div className="cp-field">
          <label htmlFor="p-c">Draft a payout from approved commissions</label>
          <select id="p-c" className="cp-select" value={creatorId} onChange={(e) => setCreatorId(e.target.value)} required>
            <option value="">Choose a creator…</option>
            {creators.data?.creators
              .filter((c) => ["APPROVED", "ACTIVE", "SUSPENDED"].includes(c.status))
              .map((c) => (
                <option key={c._id} value={c._id}>
                  {c.displayName} ({c.creatorId})
                </option>
              ))}
          </select>
        </div>
        <button className="cp-btn">Draft payout</button>
      </form>
      <Notice state={s} />
      {s.data && (
        <table className="cp-table">
          <thead>
            <tr>
              <th>Created</th>
              <th>Creator</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Method / ref</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {s.data.payouts.map((p) => (
              <tr key={p._id}>
                <td>{fmtDate(p.createdAt)}</td>
                <td>{p.creator?.displayName}</td>
                <td>{usd(p.amountCents)}</td>
                <td>
                  <span className={`cp-status ${p.status}`}>{p.status}</span>
                </td>
                <td>{p.method ? `${p.method} · ${p.reference}` : "—"}</td>
                <td>
                  {p.status === "draft" && (
                    <div className="cp-admin-actions">
                      <button className="cp-btn small" onClick={() => markPaid(p)}>
                        Record paid
                      </button>
                      <button className="cp-btn small ghost" onClick={async () => (await act(() => api(`/admin/payouts/${p._id}/cancel`, { method: "POST" }), "Cancelled")) && reload()}>
                        Cancel
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Campaigns({ act }) {
  const [s, reload] = useAsync(() => api("/admin/campaigns"), []);
  const [form, setForm] = useState({ name: "", adSpendDollars: "" });
  const save = async (e) => {
    e.preventDefault();
    (await act(() => api("/admin/campaigns", { method: "POST", body: form }), "Campaign saved")) && reload();
  };
  return (
    <div className="cp-admin-grid">
      <div className="cp-note">
        Tag your TikTok ad's landing URL with <b>utm_source=tiktok&amp;utm_campaign=&lt;campaign slug&gt;</b> pointing at instapass.shop/creators.
        Applicants are tied to the campaign automatically. Enter spend from TikTok Ads Manager to get cost per creator.
      </div>
      <form className="cp-inline-form" onSubmit={save}>
        <div className="cp-field">
          <label htmlFor="c-n">Campaign name</label>
          <input id="c-n" className="cp-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="JAY-Z SoFi pilot" required />
        </div>
        <div className="cp-field" style={{ flex: "0 1 160px" }}>
          <label htmlFor="c-s">Ad spend ($)</label>
          <input id="c-s" className="cp-input" inputMode="decimal" value={form.adSpendDollars} onChange={(e) => setForm({ ...form, adSpendDollars: e.target.value })} />
        </div>
        <button className="cp-btn">Save</button>
      </form>
      <Notice state={s} />
      {s.data && (
        <div className="cp-table-scroll">
          <table className="cp-table">
            <thead>
              <tr>
                <th>Campaign (slug)</th>
                <th>Spend</th>
                <th>Applications</th>
                <th>Approved</th>
                <th>Active</th>
                <th>$/application</th>
                <th>$/approved</th>
                <th>$/active</th>
                <th>Tickets</th>
                <th>Revenue</th>
                <th>Commissions</th>
              </tr>
            </thead>
            <tbody>
              {s.data.campaigns.map((c) => (
                <tr key={c._id}>
                  <td>
                    {c.name}
                    <div className="cp-fine" style={{ margin: 0 }}>{c.slug}</div>
                  </td>
                  <td>{usd(c.adSpendCents)}</td>
                  <td>{c.funnel.applications}</td>
                  <td>{c.funnel.approved}</td>
                  <td>{c.funnel.active}</td>
                  <td>{c.funnel.costPerApplicationCents != null ? usd(c.funnel.costPerApplicationCents) : "—"}</td>
                  <td>{c.funnel.costPerApprovedCents != null ? usd(c.funnel.costPerApprovedCents) : "—"}</td>
                  <td>{c.funnel.costPerActiveCents != null ? usd(c.funnel.costPerActiveCents) : "—"}</td>
                  <td>{c.funnel.ticketsSold}</td>
                  <td>{usd(c.funnel.attributedRevenueCents)}</td>
                  <td>{usd(c.funnel.commissionsCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Analytics() {
  const [groupBy, setGroupBy] = useState("creator");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [s] = useAsync(() => api(`/admin/analytics?groupBy=${groupBy}${from ? `&from=${from}` : ""}${to ? `&to=${to}T23:59:59` : ""}`), [groupBy, from, to]);
  const [creators] = useAsync(() => api("/admin/creators"), []);
  const [events] = useAsync(() => api("/admin/events"), []);
  const name = (id) => {
    if (groupBy === "creator") return creators.data?.creators.find((c) => c._id === id)?.displayName || id;
    if (groupBy === "event") return events.data?.events.find((e) => e._id === id)?.name || id;
    return id || "—";
  };
  return (
    <div className="cp-admin-grid">
      <div className="cp-inline-form">
        <div className="cp-field">
          <label htmlFor="a-g">Group by</label>
          <select id="a-g" className="cp-select" value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
            <option value="creator">Creator</option>
            <option value="event">Event</option>
            <option value="city">City</option>
            <option value="campaign">Campaign</option>
            <option value="channel">Referral source (clicks)</option>
          </select>
        </div>
        <div className="cp-field">
          <label htmlFor="a-f">From</label>
          <input id="a-f" type="date" className="cp-input" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className="cp-field">
          <label htmlFor="a-t">To</label>
          <input id="a-t" type="date" className="cp-input" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <button className="cp-btn small ghost" type="button" onClick={() => downloadAuthed("/admin/export/attributions.csv", "instapass-attributions.csv")}>
          Export attributions CSV
        </button>
      </div>
      <Notice state={s} />
      {s.data &&
        (groupBy === "channel" ? (
          <table className="cp-table">
            <thead>
              <tr>
                <th>Source</th>
                <th>Clicks + scans</th>
              </tr>
            </thead>
            <tbody>
              {s.data.rows.map((r) => (
                <tr key={r._id}>
                  <td>{r._id}</td>
                  <td>{r.clicks}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table className="cp-table">
            <thead>
              <tr>
                <th>{groupBy}</th>
                <th>Orders</th>
                <th>Tickets</th>
                <th>Revenue</th>
                <th>Net fee</th>
                <th>Commission</th>
                <th>Net contribution</th>
              </tr>
            </thead>
            <tbody>
              {s.data.rows.map((r) => (
                <tr key={String(r._id)}>
                  <td>{name(r._id)}</td>
                  <td>{r.orders}</td>
                  <td>{r.tickets}</td>
                  <td>{usd(r.revenueCents)}</td>
                  <td>{usd(r.netFeeCents)}</td>
                  <td>{usd(r.commissionCents)}</td>
                  <td>{usd(r.netFeeCents - r.commissionCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ))}
      {s.data && !s.data.rows.length && <p className="cp-fine">No verified data for this range yet.</p>}
    </div>
  );
}

function Audit() {
  const [s] = useAsync(() => api("/admin/audit"), []);
  return (
    <>
      <Notice state={s} />
      {s.data && (
        <div className="cp-table-scroll">
          <table className="cp-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Who</th>
                <th>Action</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              {s.data.entries.map((e) => (
                <tr key={e._id}>
                  <td>{new Date(e.createdAt).toLocaleString("en-US")}</td>
                  <td>{e.actor ? `${e.actor.firstName} ${e.actor.lastName}` : e.actorType}</td>
                  <td>{e.action}</td>
                  <td style={{ maxWidth: 420, wordBreak: "break-word" }}>{e.detail ? JSON.stringify(e.detail) : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
